import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Group, Table, Text } from "@mantine/core";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

export default function PropertyRequests() {
  const axiosPrivate = useAxiosPrivate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [requests, setRequests] = useState([]);
  const [actingId, setActingId] = useState(null);

  const loadRequests = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosPrivate.get("/properties/requests");
      const list = Array.isArray(res?.data?.requests) ? res.data.requests : [];
      setRequests(list);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to load requests";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const approve = async (id) => {
    setActingId(id);
    setError("");
    try {
      await axiosPrivate.post(`/properties/requests/${id}/approve`);
      await loadRequests();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to approve request";
      setError(msg);
    } finally {
      setActingId(null);
    }
  };

  const decline = async (id) => {
    setActingId(id);
    setError("");
    try {
      await axiosPrivate.post(`/properties/requests/${id}/decline`);
      await loadRequests();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to decline request";
      setError(msg);
    } finally {
      setActingId(null);
    }
  };

  const rows = useMemo(() => {
    return (requests || []).map((r) => {
      const status = String(r?.status || "").toLowerCase();
      const statusColor = status === "approved" ? "green" : status === "rejected" ? "red" : "yellow";
      const canAct = status === "pending";

      let names = [];
      if (Array.isArray(r?.propertyNames)) {
        names = r.propertyNames;
      } else if (typeof r?.propertyNames === "string") {
        try {
          const parsed = JSON.parse(r.propertyNames);
          names = Array.isArray(parsed) ? parsed : [String(parsed)];
        } catch {
          names = [r.propertyNames];
        }
      }

      return (
        <tr key={r.id}>
          <td>{r.id}</td>
          <td>{r.userId}</td>
          <td>{r.mainPropertyId}</td>
          <td>{r.requestedCount}</td>
          <td>
            <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
              {names.length ? names.join("\n") : ""}
            </Text>
          </td>
          <td>
            <Badge color={statusColor} variant="filled">
              {status || "unknown"}
            </Badge>
          </td>
          <td>
            <Group spacing="xs">
              <Button
                size="xs"
                color="green"
                onClick={() => approve(r.id)}
                disabled={!canAct || actingId === r.id}
              >
                Approve
              </Button>
              <Button
                size="xs"
                color="red"
                variant="outline"
                onClick={() => decline(r.id)}
                disabled={!canAct || actingId === r.id}
              >
                Decline
              </Button>
            </Group>
          </td>
        </tr>
      );
    });
  }, [requests, actingId]);

  if (loading) return <Text>Loading...</Text>;

  return (
    <div style={{ padding: 20 }}>
      <Group position="apart" mb="md">
        <Text size="xl" weight={700}>
          Property Requests
        </Text>
        <Button variant="outline" onClick={loadRequests}>
          Refresh
        </Button>
      </Group>

      {error && (
        <Text color="red" mb="md">
          {error}
        </Text>
      )}

      <Table striped highlightOnHover withBorder withColumnBorders>
        <thead>
          <tr>
            <th>ID</th>
            <th>User ID</th>
            <th>Main Property ID</th>
            <th>Count</th>
            <th>Names</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </Table>
    </div>
  );
}
